import { formatParisDateTime, type SchedulePresetInput } from '@flux/shared';
import { ChevronRight } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { cn } from '@/lib/cn.js';

/**
 * Éditeur de planification partagé par le Suivi Martelet et Pennylane
 * (`02` §12.3, `02` §12.4), refondu le 10/09/2026.
 *
 * Le **préréglage** est la surface par défaut : « chaque jour à 8 h 00 » est ce
 * que l'utilisateur veut dire, pas `0 8 * * *`. L'expression cron reste
 * accessible, repliée sous « Avancé » — elle sert aux cas que les préréglages
 * ne couvrent pas, et elle est la source de vérité côté serveur.
 */
const PRESET_KINDS = ['every-minutes', 'hourly', 'every-hours', 'daily', 'weekly'] as const;

type PresetKind = (typeof PRESET_KINDS)[number];

const PRESET_KIND_LABEL: Record<PresetKind, string> = {
  'every-minutes': 'Toutes les N minutes',
  hourly: 'Toutes les heures',
  'every-hours': 'Toutes les N heures (plage)',
  daily: 'Chaque jour',
  weekly: 'Chaque semaine',
};

const WEEKDAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function dailyFromCron(cron: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(cron);
  if (!match) return null;
  return { hour: Number(match[2]), minute: Number(match[1]) };
}

function buildPreset(
  kind: PresetKind,
  fields: Record<string, number>,
  weekdays: number[],
): SchedulePresetInput {
  switch (kind) {
    case 'every-minutes':
      return { kind, minutes: fields.minutes ?? 15 };
    case 'hourly':
      return { kind, minute: fields.minute ?? 0 };
    case 'every-hours':
      return {
        kind,
        hours: fields.hours ?? 2,
        startHour: fields.startHour ?? 8,
        endHour: fields.endHour ?? 20,
        minute: fields.minute ?? 0,
      };
    case 'daily':
      return { kind, hour: fields.hour ?? 8, minute: fields.minute ?? 0 };
    case 'weekly':
      return {
        kind,
        weekdays: weekdays.length > 0 ? weekdays : [1],
        hour: fields.hour ?? 8,
        minute: fields.minute ?? 0,
      };
  }
}

export interface ScheduleEditorProps {
  /** Phrase lisible de la planification courante (`scheduleDescription`). */
  description: string;
  /** Expression cron courante, préremplie dans le repli « Avancé ». */
  cron: string;
  /** Prochaines exécutions calculées par le serveur, en ISO. */
  nextRuns: readonly string[];
  isSaving: boolean;
  onSaveCron: (cron: string) => void;
  onSavePreset: (preset: SchedulePresetInput) => void;
}

export function ScheduleEditor({
  description,
  cron,
  nextRuns,
  isSaving,
  onSaveCron,
  onSavePreset,
}: ScheduleEditorProps): JSX.Element {
  const [cronDraft, setCronDraft] = useState(cron);
  const [presetKind, setPresetKind] = useState<PresetKind>('daily');
  const [presetFields, setPresetFields] = useState<Record<string, number>>(dailyFromCron(cron) ?? { hour: 8, minute: 0 });
  const [presetWeekdays, setPresetWeekdays] = useState<number[]>([1]);
  const [editingMode, setEditingMode] = useState<'preset' | 'cron'>(dailyFromCron(cron) ? 'preset' : 'cron');

  // Inactifs tant que rien n'a changé : le retour à l'état inactif après un
  // clic confirme la sauvegarde. Seul le préréglage quotidien se compare
  // directement à l'expression enregistrée.
  const savedDaily = dailyFromCron(cron);
  const presetUnchanged =
    presetKind === 'daily' &&
    savedDaily !== null &&
    presetFields.hour === savedDaily.hour &&
    presetFields.minute === savedDaily.minute;
  const cronUnchanged = cronDraft.trim() === cron;

  useEffect(() => {
    setCronDraft(cron);
    const parsed = dailyFromCron(cron);
    if (parsed) setPresetFields(parsed);
  }, [cron]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium">Planification : {description}</p>
        <p className="text-xs text-muted-foreground">
          Prochaines exécutions :{' '}
          <span className="tabular-nums">
            {nextRuns.length > 0
              ? nextRuns.map((iso) => formatParisDateTime(new Date(iso))).join(', ')
              : 'aucune'}
          </span>
        </p>
      </div>

      <fieldset disabled={editingMode === 'cron' || isSaving} className="flex flex-col gap-3 disabled:opacity-50">
        <div className="flex flex-wrap items-end gap-3">
          <Select
            value={presetKind}
            onValueChange={(next) => {
              setPresetKind(next as PresetKind);
            }}
          >
            <SelectTrigger className="w-full sm:w-64" aria-label="Type de préréglage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {PRESET_KIND_LABEL[kind]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {presetKind === 'every-minutes' ? (
            <NumberField
              label="Toutes les (minutes)"
              value={presetFields.minutes ?? 15}
              min={1}
              max={59}
              onChange={(value) => {
                setPresetFields((prev) => ({ ...prev, minutes: value }));
              }}
            />
          ) : null}
          {presetKind === 'hourly' ? (
            <NumberField
              label="À la minute"
              value={presetFields.minute ?? 0}
              min={0}
              max={59}
              onChange={(value) => {
                setPresetFields((prev) => ({ ...prev, minute: value }));
              }}
            />
          ) : null}
          {presetKind === 'every-hours' ? (
            <>
              <NumberField
                label="Toutes les (heures)"
                value={presetFields.hours ?? 2}
                min={1}
                max={23}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, hours: value }));
                }}
              />
              <NumberField
                label="Depuis"
                value={presetFields.startHour ?? 8}
                min={0}
                max={23}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, startHour: value }));
                }}
              />
              <NumberField
                label="Jusqu’à"
                value={presetFields.endHour ?? 20}
                min={0}
                max={23}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, endHour: value }));
                }}
              />
              <NumberField
                label="Minute"
                value={presetFields.minute ?? 0}
                min={0}
                max={59}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, minute: value }));
                }}
              />
            </>
          ) : null}
          {presetKind === 'daily' || presetKind === 'weekly' ? (
            <>
              <NumberField
                label="Heure"
                value={presetFields.hour ?? 8}
                min={0}
                max={23}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, hour: value }));
                }}
              />
              <NumberField
                label="Minute"
                value={presetFields.minute ?? 0}
                min={0}
                max={59}
                onChange={(value) => {
                  setPresetFields((prev) => ({ ...prev, minute: value }));
                }}
              />
            </>
          ) : null}
        </div>

        {presetKind === 'weekly' ? (
          <WeekdayToggles
            selected={presetWeekdays}
            onToggle={(index) => {
              setPresetWeekdays((prev) =>
                prev.includes(index)
                  ? prev.filter((day) => day !== index)
                  : [...prev, index].sort(),
              );
            }}
          />
        ) : null}

        <div>
          <Button
            type="button"
            disabled={presetUnchanged}
            isLoading={isSaving}
            onClick={() => {
              onSavePreset(buildPreset(presetKind, presetFields, presetWeekdays));
            }}
          >
            Enregistrer la planification
          </Button>
        </div>
      </fieldset>

      <div className="border-t border-border pt-4">
        <button type="button" aria-expanded={editingMode === 'cron'} onClick={() => setEditingMode(editingMode === 'cron' ? 'preset' : 'cron')}
          className="flex w-fit items-center gap-1.5 rounded-sm text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronRight
            className={cn('size-4 transition-transform', editingMode === 'cron' && 'rotate-90')}
            aria-hidden="true"
          />
          Avancé — expression cron
        </button>
        {editingMode === 'cron' ? <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field label="Expression cron" htmlFor="schedule-cron" className="w-full sm:w-64">
            <Input
              id="schedule-cron"
              value={cronDraft}
              className="font-mono"
              onChange={(event) => {
                setCronDraft(event.target.value);
              }}
            />
          </Field>
          <Button
            type="button"
            variant="outline"
            disabled={cronUnchanged}
            isLoading={isSaving}
            onClick={() => {
              onSaveCron(cronDraft);
            }}
          >
            Enregistrer
          </Button>
        </div> : null}
      </div>
    </div>
  );
}

/** Jours de la semaine du préréglage hebdomadaire : boutons bascule (`aria-pressed`). */
export function WeekdayToggles({
  selected,
  onToggle,
}: {
  selected: readonly number[];
  onToggle: (index: number) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1">
      {WEEKDAY_LABELS.map((label, index) => {
        const pressed = selected.includes(index);
        return (
          <button
            key={label}
            type="button"
            aria-pressed={pressed}
            className={cn(
              'inline-flex h-8 min-w-11 items-center justify-center rounded-md border px-2 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              pressed
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-card text-foreground hover:bg-accent',
            )}
            onClick={() => {
              onToggle(index);
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}): JSX.Element {
  const id = useId();
  return (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        className="w-24"
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isInteger(next)) {
            onChange(next);
          }
        }}
      />
    </Field>
  );
}
